import { useState } from 'react';
import { useForgeNavigate } from '../components/layout/ForgeTransition';
import { PageWrapper } from '../components/layout/PageWrapper';
import { Navbar } from '../components/layout/Navbar';
import { ProgressTracker } from '../components/layout/ProgressTracker';
import { Card } from '../components/shared/Card';
import { Button } from '../components/shared/Button';
import { ExternalLink, GraduationCap, Layers, TrendingUp } from 'lucide-react';

const CLASSROOM_URL = 'https://bit.ly/3lQnMi2';
const WHOP_URL = 'https://whop.com/home-feed/';
const READINESS_URL = 'https://like-tabletop-a70.notion.site/Readiness-Progression-2148f14330dd80418679d79e6939310f?source=copy_link';

type Tab = 'classroom' | 'indicators' | 'readiness';

export function ResourceHub() {
  const forgeNavigate = useForgeNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('classroom');

  const tabs: { id: Tab; label: string; icon: React.ElementType }[] = [
    { id: 'classroom', label: 'Classroom', icon: GraduationCap },
    { id: 'indicators', label: 'Indicator Suite', icon: Layers },
    { id: 'readiness', label: 'Readiness', icon: TrendingUp },
  ];

  return (
    <PageWrapper warmth={0.15}>
      <Navbar />
      <main className="min-h-screen pt-16">
        <ProgressTracker currentStep={3} />

        <div className="max-w-[660px] mx-auto px-8 pb-16">
          <h1 className="text-2xl md:text-3xl font-bold font-mono tracking-wide text-shimmer text-center mb-3">Your Toolkit</h1>
          <p className="text-[13px] font-mono text-white/35 text-center mb-8 tracking-wide">
            Everything you need before entering the arena.
          </p>

          {/* Tab switcher */}
          <div className="flex gap-1 mb-8 p-1 bg-white/[0.02] border border-white/[0.05] rounded-lg">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-[10px] font-semibold font-mono tracking-wider uppercase rounded-md transition-all duration-300 cursor-pointer ${
                    activeTab === tab.id
                      ? 'bg-white/[0.08] text-white border border-white/[0.12] shadow-[0_0_10px_rgba(255,255,255,0.03)]'
                      : 'text-white/35 hover:text-white/60 border border-transparent'
                  }`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* ── Classroom Tab ── */}
          {activeTab === 'classroom' && (
            <div
              className="flex flex-col gap-6"
              style={{ animation: 'card-enter 0.5s cubic-bezier(0.16,1,0.3,1) both' }}
            >
              <Card bento>
                <div className="flex flex-col items-center gap-4 py-4">
                  <div className="w-14 h-14 rounded-full bg-[#E84040]/10 border border-[#E84040]/20 flex items-center justify-center">
                    <GraduationCap className="w-7 h-7 text-[#E84040]" />
                  </div>
                  <h3 className="text-base font-bold font-mono text-white/90 text-center">Inevitrade Academy</h3>
                  <p className="text-sm font-mono text-white/35 text-center leading-relaxed max-w-[440px]">
                    The full Inevitrade course — strategy deep dives, trade breakdowns, and everything you need to master TCL and SMOG.
                  </p>
                </div>
              </Card>

              <Button
                onClick={() => window.open(CLASSROOM_URL, '_blank')}
                className="w-full flex items-center justify-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                Go to Classroom
              </Button>
            </div>
          )}

          {/* ── Indicator Suite Tab ── */}
          {activeTab === 'indicators' && (
            <div
              className="flex flex-col gap-6"
              style={{ animation: 'card-enter 0.5s cubic-bezier(0.16,1,0.3,1) both' }}
            >
              {/* Screenshot guide */}
              <div className="rounded-xl overflow-hidden border border-white/[0.06]">
                <img
                  src="/images/whop-claim-guide.png"
                  alt="How to claim indicators on Whop — step by step guide showing TradingView Lifetime Indicators page"
                  className="w-full h-auto"
                />
              </div>

              <Card bento>
                <h3 className="text-base font-bold font-mono text-white/90 mb-5 text-center">How to Claim Your Indicators</h3>

                <div className="flex flex-col gap-4">
                  {/* Step 1 */}
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-[#4A9EFF]/10 border border-[#4A9EFF]/20 flex items-center justify-center shrink-0">
                      <span className="text-xs font-bold font-mono text-[#4A9EFF]">1</span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold font-mono text-white/80">Open the Whop</p>
                      <p className="text-xs font-mono text-white/35 mt-0.5 leading-relaxed">
                        Click the <span className="text-white/60">Inevitrade</span> icon in the left sidebar to open your Whop dashboard.
                      </p>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-[#4A9EFF]/10 border border-[#4A9EFF]/20 flex items-center justify-center shrink-0">
                      <span className="text-xs font-bold font-mono text-[#4A9EFF]">2</span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold font-mono text-white/80">Open the Indicators</p>
                      <p className="text-xs font-mono text-white/35 mt-0.5 leading-relaxed">
                        Click <span className="text-white/60">TradingView Lifetime Indicators</span> in the left menu.
                      </p>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-[#4A9EFF]/10 border border-[#4A9EFF]/20 flex items-center justify-center shrink-0">
                      <span className="text-xs font-bold font-mono text-[#4A9EFF]">3</span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold font-mono text-white/80">Enter your TradingView username</p>
                      <p className="text-xs font-mono text-white/35 mt-0.5 leading-relaxed">
                        Type your username and click <span className="text-white/60">Update</span>.
                      </p>
                    </div>
                  </div>

                  {/* Step 4 */}
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-[#3DBF7A]/10 border border-[#3DBF7A]/20 flex items-center justify-center shrink-0">
                      <span className="text-xs font-bold font-mono text-[#3DBF7A]">4</span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold font-mono text-white/80">Claim Indicators</p>
                      <p className="text-xs font-mono text-white/35 mt-0.5 leading-relaxed">
                        Hit the blue <span className="text-white/60">Claim Indicators</span> button to unlock all three:
                      </p>
                      <div className="flex flex-col gap-1.5 mt-2">
                        <span className="text-xs font-mono text-[#4A9EFF]">• TCL Calculator</span>
                        <span className="text-xs font-mono text-[#4A9EFF]">• Futures TCL Calculator</span>
                        <span className="text-xs font-mono text-[#3DBF7A]">• IT SMOG/G2</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              <Button
                onClick={() => window.open(WHOP_URL, '_blank')}
                className="w-full flex items-center justify-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                Go to Whop
              </Button>
            </div>
          )}

          {/* ── Readiness Tracker Tab ── */}
          {activeTab === 'readiness' && (
            <div
              className="flex flex-col gap-6"
              style={{ animation: 'card-enter 0.5s cubic-bezier(0.16,1,0.3,1) both' }}
            >
              <Card bento>
                <div className="flex flex-col items-center gap-4 py-4">
                  <div className="w-14 h-14 rounded-full bg-[#3DBF7A]/10 border border-[#3DBF7A]/20 flex items-center justify-center">
                    <TrendingUp className="w-7 h-7 text-[#3DBF7A]" />
                  </div>
                  <h3 className="text-base font-bold font-mono text-white/90 text-center">Readiness Progression Tracker</h3>
                  <p className="text-sm font-mono text-white/35 text-center leading-relaxed max-w-[440px]">
                    Track your journey from Foundation to Funded. Open the tracker to see where you are and what's next.
                  </p>
                </div>
              </Card>

              <Button
                onClick={() => window.open(READINESS_URL, '_blank')}
                className="w-full flex items-center justify-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                Open Readiness Tracker
              </Button>
            </div>
          )}

          <div className="mt-8 flex justify-center">
            <Button onClick={() => forgeNavigate('/stats/backtest', 3)}>
              Ready to enter stats
            </Button>
          </div>
        </div>
      </main>
    </PageWrapper>
  );
}
